// Define Wishlist class only if it doesn't already exist
if (typeof Wishlist === 'undefined') {
  window.Wishlist = class Wishlist {
    constructor() {
      this.wishlist = JSON.parse(localStorage.getItem('wishlist') || '{}');
      this.error = false;

      this.elements = {
        count: document.getElementById('WishlistCount'),
        countText: document.getElementById('WishlistCountText'),
        drawer: document.getElementById('WishlistDrawer'),
        trigger: document.getElementById('WishlistTrigger')
      };

      this.init();
    }

    init() {
      // Validate and clean orphan items first, then update count
      this.validateAndCleanWishlist().then(() => {
        this.updateCount();
        this.updateDrawerState();
        this.initializeWishlistButtons();
      });
      this.setupEventListeners();
    }

    // Validate all wishlist items and remove orphans (products that no longer exist)
    async validateAndCleanWishlist() {
      const productIds = Object.keys(this.wishlist);
      if (productIds.length === 0) return;

      const itemsToRemove = [];

      // Check each item in parallel
      const validationPromises = productIds.map(async (productId) => {
        const item = this.wishlist[productId];
        if (!item || !item.handle) {
          // Item is malformed, mark for removal
          itemsToRemove.push(productId);
          return;
        }

        try {
          const response = await fetch(`/products/${item.handle}.js`, {
            method: 'HEAD' // Use HEAD request for faster validation
          });

          if (!response.ok) {
            // Product doesn't exist (404) or is unavailable, mark for removal
            console.warn(`Wishlist cleanup: Product "${item.handle}" no longer exists, removing from wishlist`);
            itemsToRemove.push(productId);
          }
        } catch (error) {
          // Network error - don't remove, might be temporary
          console.warn(`Wishlist validation: Could not verify product "${item.handle}", keeping in wishlist`);
        }
      });

      await Promise.all(validationPromises);

      // Remove invalid items
      if (itemsToRemove.length > 0) {
        itemsToRemove.forEach(productId => {
          delete this.wishlist[productId];
        });

        this.saveWishlist();
        console.log(`Wishlist cleanup: Removed ${itemsToRemove.length} orphaned item(s)`);

        // Dispatch update event to notify other components
        this.dispatchUpdateEvent();
      }
    }

    setupEventListeners() {
      // Wishlist button clicks using event delegation
      document.addEventListener('click', async (e) => {
        const button = e.target.closest('[data-wishlist-button]');
        if (!button) return;

        e.preventDefault();
        e.stopPropagation();

        const productId = button.getAttribute('data-product-id');
        if (productId) {
          await this.toggleProduct(productId, button);
        }
      });

      // Wishlist trigger click
      if (this.elements.trigger) {
        this.elements.trigger.addEventListener('click', () => this.openDrawer());
      }

      // Add all to cart button click - simplified approach
      document.addEventListener('click', async (e) => {
        // Check if we clicked on the button or any of its children
        let clickedButton = null;

        // Check the target itself
        if (e.target.id === 'WishlistDrawer-AddAllToCart') {
          clickedButton = e.target;
        }
        // Check if target is inside the button
        else if (e.target.closest('#WishlistDrawer-AddAllToCart')) {
          clickedButton = e.target.closest('#WishlistDrawer-AddAllToCart');
        }
        // Manual parent traversal as fallback
        else {
          let element = e.target;
          while (element && element !== document.body) {
            if (element.id === 'WishlistDrawer-AddAllToCart') {
              clickedButton = element;
              break;
            }
            element = element.parentElement;
          }
        }

        if (clickedButton) {
          e.preventDefault();
          e.stopPropagation();
          await this.addAllToCart();
        }
      });

      // Listen for wishlist changes from other components
      window.addEventListener('wishlist:updated', async () => {
        this.updateCount();
        // Update wishlist buttons state without rebuilding drawer
        this.initializeWishlistButtons();
      });

      // Listen for variant changes on product page
      this.setupVariantChangeListener();

      // Re-initialize wishlist buttons when new content is loaded (for AJAX/navigation)
      this.initializeWishlistButtons();
    }

    setupVariantChangeListener() {
      // Listen for variant changes on product page
      document.addEventListener('variant:change', (event) => {
        const newVariant = event.detail.variant;
        if (newVariant && newVariant.id) {
          // Update wishlist button with new variant ID
          const wishlistButton = document.querySelector('[data-wishlist-button][data-is-product-page="true"]');
          if (wishlistButton) {
            wishlistButton.setAttribute('data-current-variant-id', newVariant.id);

            // If this product is in the wishlist, update the stored variant ID
            const productId = wishlistButton.getAttribute('data-product-id');
            if (productId && this.wishlist[productId]) {
              this.wishlist[productId].selectedVariantId = newVariant.id;
              this.saveWishlist();
            }
          }
        }
      });

      // Also listen for variant selector changes
      document.addEventListener('change', (event) => {
        const variantSelector = event.target.closest('variant-selects');
        if (variantSelector) {
          const selectedVariantScript = variantSelector.querySelector('script[data-selected-variant]');
          if (selectedVariantScript) {
            try {
              const selectedVariant = JSON.parse(selectedVariantScript.textContent);
              if (selectedVariant && selectedVariant.id) {
                // Update wishlist button with new variant ID
                const wishlistButton = document.querySelector('[data-wishlist-button][data-is-product-page="true"]');
                if (wishlistButton) {
                  wishlistButton.setAttribute('data-current-variant-id', selectedVariant.id);

                  // If this product is in the wishlist, update the stored variant ID
                  const productId = wishlistButton.getAttribute('data-product-id');
                  if (productId && this.wishlist[productId]) {
                    this.wishlist[productId].selectedVariantId = selectedVariant.id;
                    this.saveWishlist();
                  }
                }
              }
            } catch (e) {
              console.warn('Could not parse selected variant:', e);
            }
          }
        }
      });
    }

    async toggleProduct(productId, button) {
      if (!productId || !button) return;

      const productIdInt = parseInt(productId);
      if (isNaN(productIdInt)) return;

      // Get current variant ID if available
      let currentVariantId = null;
      if (button.getAttribute('data-is-product-page') === 'true') {
        currentVariantId = button.getAttribute('data-current-variant-id');
      }

      // Create wishlist key for checking
      const wishlistKey = currentVariantId ? `${productIdInt}-${currentVariantId}` : productIdInt;

      // Check if this exact product+variant combination is in wishlist
      let isInWishlist = this.wishlist.hasOwnProperty(wishlistKey);

      // If not found by exact key, check if any variant of this product is in wishlist
      if (!isInWishlist) {
        const productKeys = Object.keys(this.wishlist).filter(key =>
          key.startsWith(`${productIdInt}-`) || key === productIdInt.toString()
        );
        isInWishlist = productKeys.length > 0;
      }

      if (isInWishlist) {
        // Remove the product completely from wishlist (regardless of variant)
        this.removeProduct(productIdInt);
        button.classList.remove('is-in-wishlist');
        button.setAttribute('aria-label', 'Add to wishlist');

        // Remove all items for this product from drawer visually
        const wishlistItems = this.elements.drawer?.querySelector('#WishlistItems');
        if (wishlistItems) {
          // Remove all items that start with this product ID
          const itemsToRemove = wishlistItems.querySelectorAll(`[id^="WishlistDrawer-Item-${productIdInt}"]`);
          itemsToRemove.forEach(item => item.remove());
        }

        // Update drawer state if empty
        const isEmpty = Object.keys(this.wishlist).length === 0;
        if (isEmpty) {
          this.showEmptyState();
        }
      } else {
        this.addProduct(productIdInt, button);
        button.classList.add('is-in-wishlist');
        button.setAttribute('aria-label', 'Remove from wishlist');

        // Add item to drawer visually if drawer is open
        if (this.elements.drawer) {
          // Small delay to ensure wishlist is updated
          await new Promise(resolve => setTimeout(resolve, 50));

          // Find the actual key that was used to store the item
          let actualKey = null;
          if (currentVariantId) {
            actualKey = `${productIdInt}-${currentVariantId}`;
          } else {
            actualKey = productIdInt;
          }

          // If the exact key doesn't exist, find any key for this product
          if (!this.wishlist[actualKey]) {
            const productKeys = Object.keys(this.wishlist).filter(key =>
              key.startsWith(`${productIdInt}-`) || key === productIdInt.toString()
            );
            if (productKeys.length > 0) {
              actualKey = productKeys[0];
            }
          }

          // Check if the item exists in wishlist with the found key
          if (this.wishlist[actualKey]) {
            const item = { id: actualKey, ...this.wishlist[actualKey] };
            await this.addSingleItemToDrawer(item);
          }

          // Ensure drawer state is correct after adding item
          if (this.elements.drawer.classList.contains('is-empty')) {
            this.hideEmptyState();
          }
        }
      }

      this.updateCount();
      // Don't call updateDrawerState here to prevent unnecessary rebuilding
      this.dispatchUpdateEvent();

      // Update all buttons for this product across the page
      document.querySelectorAll(`[data-wishlist-button][data-product-id="${productId}"]`).forEach(btn => {
        if (btn !== button) {
          if (isInWishlist) {
            btn.classList.remove('is-in-wishlist');
            btn.setAttribute('aria-label', 'Add to wishlist');
          } else {
            btn.classList.add('is-in-wishlist');
            btn.setAttribute('aria-label', 'Remove from wishlist');
          }
        }
      });

      // Update total
      this.updateWishlistTotal();
    }

    addProduct(productId, button = null) {
      // Get selected variant ID if on product page
      let selectedVariantId = null;
      if (button && button.getAttribute('data-is-product-page') === 'true') {
        // Try to get the current variant from the variant selector
        const variantSelectorId = button.getAttribute('data-variant-selector-id');
        if (variantSelectorId) {
          const variantSelector = document.getElementById(variantSelectorId);
          if (variantSelector) {
            const selectedVariantScript = variantSelector.querySelector('script[data-selected-variant]');
            if (selectedVariantScript) {
              try {
                const selectedVariant = JSON.parse(selectedVariantScript.textContent);
                selectedVariantId = selectedVariant.id;
              } catch (e) {
                console.warn('Could not parse selected variant:', e);
              }
            }
          }
        }

        // Fallback to the current variant ID from the button
        if (!selectedVariantId) {
          const variantIdAttr = button.getAttribute('data-current-variant-id');
          selectedVariantId = variantIdAttr ? parseInt(variantIdAttr) : null;
        }
      }

      // Create a unique key for this product+variant combination
      const wishlistKey = selectedVariantId ? `${productId}-${selectedVariantId}` : productId;

      // Check if this exact product+variant combination is already in wishlist
      if (this.wishlist.hasOwnProperty(wishlistKey)) {
        return; // Don't add duplicate
      }

      // If product exists but with different variant, remove the old entry
      if (this.wishlist.hasOwnProperty(productId)) {
        delete this.wishlist[productId];
      }

      const productHandle = button ? button.getAttribute('data-product-handle') : null;
      if (productHandle) {
        // Check if product is in presale by looking for presale-related attributes
        const isPresale = button ?
          (button.getAttribute('data-product-presale') === 'true' ||
            button.closest('[data-presale]') !== null) : false;

        this.wishlist[wishlistKey] = {
          handle: productHandle,
          addedAt: new Date().toISOString(),
          presale: isPresale,
          selectedVariantId: selectedVariantId
        };

        this.saveWishlist();
        this.updateCount();
        // Only dispatch event if not called from toggleProduct
        if (!button || !button.hasAttribute('data-wishlist-button')) {
          this.dispatchUpdateEvent();
        }
      }
    }

    removeProduct(productId, variantId = null) {
      // If variantId is provided, remove only that specific variant
      if (variantId) {
        const wishlistKey = `${productId}-${variantId}`;
        if (this.wishlist.hasOwnProperty(wishlistKey)) {
          delete this.wishlist[wishlistKey];
          this.saveWishlist();
          this.updateCount();
          this.dispatchUpdateEvent();
          return;
        }
      }

      // If no variantId provided or variant not found, remove all variants of this product
      const productKeys = Object.keys(this.wishlist).filter(key =>
        key.startsWith(`${productId}-`) || key === productId.toString()
      );

      if (productKeys.length > 0) {
        // Remove all variants of this product
        productKeys.forEach(key => {
          delete this.wishlist[key];
        });
        this.saveWishlist();
        this.updateCount();
        this.dispatchUpdateEvent();
        return;
      }
    }

    saveWishlist() {
      localStorage.setItem('wishlist', JSON.stringify(this.wishlist));
    }

    updateCount() {
      const count = Object.keys(this.wishlist).length;

      if (this.elements.count && this.elements.countText) {
        const ariaElement = document.getElementById('WishlistCountAria');

        if (count > 0) {
          this.elements.count.style.display = 'flex';
          this.elements.countText.textContent = count;
          if (ariaElement) {
            ariaElement.textContent = count === 1 ? '1 artículo' : `${count} artículos`;
          }
        } else {
          this.elements.count.style.display = 'none';
        }
      }
    }

    async updateDrawerState() {
      if (!this.elements.drawer) return;

      const isEmpty = Object.keys(this.wishlist).length === 0;
      this.elements.drawer.classList.toggle('is-empty', isEmpty);

      // Update add all to cart button state
      const addAllButton = document.getElementById('WishlistDrawer-AddAllToCart');
      if (addAllButton) {
        addAllButton.disabled = isEmpty;

        // Add direct event listener as backup
        if (!addAllButton.hasAttribute('data-wishlist-direct-listener')) {
          addAllButton.setAttribute('data-wishlist-direct-listener', 'true');
          addAllButton.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await this.addAllToCart();
          });
        }
      }

      if (isEmpty) {
        this.showEmptyState();
        this.updateWishlistTotal();
      } else {
        this.hideEmptyState();
        // Force a complete rebuild of the drawer content
        await this.updateDrawerContent();
      }
    }

    showEmptyState() {
      if (!this.elements.drawer) return;

      // Add empty class to drawer
      this.elements.drawer.classList.add('is-empty');

      // Clear wishlist items
      const wishlistItems = this.elements.drawer.querySelector('#WishlistItems');
      if (wishlistItems) {
        wishlistItems.innerHTML = '';
      }

      // Show empty content
      const wishlistEmpty = this.elements.drawer.querySelector('#WishlistEmpty');
      if (wishlistEmpty) {
        wishlistEmpty.style.display = 'flex';
      }

      // Disable add all to cart button
      const addAllButton = document.getElementById('WishlistDrawer-AddAllToCart');
      if (addAllButton) {
        addAllButton.disabled = true;
      }
    }

    hideEmptyState() {
      if (!this.elements.drawer) return;

      // Remove empty class from drawer
      this.elements.drawer.classList.remove('is-empty');

      // Hide empty content
      const wishlistEmpty = this.elements.drawer.querySelector('#WishlistEmpty');
      if (wishlistEmpty) {
        wishlistEmpty.style.display = 'none';
      }

      // Enable add all to cart button
      const addAllButton = document.getElementById('WishlistDrawer-AddAllToCart');
      if (addAllButton) {
        addAllButton.disabled = false;
      }
    }

    // Update the "Add All to Cart" button state based on whether all items are archived
    updateAddAllButtonState() {
      const addAllButton = document.getElementById('WishlistDrawer-AddAllToCart');
      if (!addAllButton) return;

      const wishlistItems = this.elements.drawer?.querySelector('#WishlistItems');
      if (!wishlistItems) return;

      // Get all cart items
      const cartItems = wishlistItems.querySelectorAll('.cart-item');
      if (cartItems.length === 0) {
        // No items, use default text
        const buttonText = addAllButton.querySelector('span');
        if (buttonText) {
          buttonText.textContent = window.cartStrings?.addAllToCart || 'Add all to shopping bag';
        }
        addAllButton.disabled = true;
        return;
      }

      // Check if ALL items have the sold-out badge
      const soldOutBadges = wishlistItems.querySelectorAll('.badge.price__badge-sold-out');
      const allItemsArchived = soldOutBadges.length === cartItems.length;

      const buttonText = addAllButton.querySelector('span');
      if (buttonText) {
        if (allItemsArchived) {
          buttonText.textContent = window.cartStrings?.allItemsArchived || 'All pieces are archived';
          addAllButton.disabled = true;
        } else {
          buttonText.textContent = window.cartStrings?.addAllToCart || 'Add all to shopping bag';
          addAllButton.disabled = false;
        }
      }
    }

    async updateDrawerContent() {
      const contentElement = this.elements.drawer?.querySelector('.drawer__content');
      if (!contentElement) return;

      const wishlistItems = contentElement.querySelector('#WishlistItems');
      if (!wishlistItems) return;

      // Clear existing content completely
      wishlistItems.innerHTML = '';

      // Also remove any existing wishlist items by their IDs
      const existingItems = wishlistItems.querySelectorAll('[id^="WishlistDrawer-Item-"]');
      existingItems.forEach(item => item.remove());

      // Add wishlist items efficiently
      const productIds = Object.keys(this.wishlist);
      if (productIds.length === 0) return;

      // Create a document fragment for better performance
      const fragment = document.createDocumentFragment();

      // Track processed items to avoid duplicates
      const processedItems = new Set();

      // Process items in batches to prevent blocking
      const batchSize = 3;
      for (let i = 0; i < productIds.length; i += batchSize) {
        const batch = productIds.slice(i, i + batchSize);

        // Process batch
        const batchPromises = batch.map(async (productId, batchIndex) => {
          const item = { id: productId, ...this.wishlist[productId] };

          // Check if this item is already processed (avoid duplicates)
          if (processedItems.has(item.id)) {
            return null;
          }

          processedItems.add(item.id);
          const itemElement = await this.createWishlistItem(item, i + batchIndex);
          return itemElement;
        });

        const batchElements = await Promise.all(batchPromises);

        // Add to fragment
        batchElements.forEach(element => {
          if (element) {
            fragment.appendChild(element);
          }
        });

        // Small delay between batches to prevent blocking
        if (i + batchSize < productIds.length) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }

      // Clear the container one more time before adding new content
      wishlistItems.innerHTML = '';

      // Add all items at once
      wishlistItems.appendChild(fragment);

      // Calculate and update total
      this.updateWishlistTotal();

      // Check for presale items and show/hide reserve terms
      this.checkPresaleItemsAndShowTerms();

      // Update "Add All to Cart" button state based on archived items
      this.updateAddAllButtonState();
    }

    async createWishlistItem(item, index) {
      // Get product handle from wishlist object
      const productHandle = item.handle;
      if (!productHandle) {
        return null;
      }

      try {
        // Fetch product data from Shopify's product API
        const response = await fetch(`/products/${productHandle}.js`);
        if (!response.ok) {
          // If product doesn't exist (404) or is unavailable, remove it from wishlist
          if (response.status === 404) {
            console.warn(`Product ${productHandle} no longer exists, removing from wishlist`);
            this.removeProduct(item.id);
            return null;
          }
          throw new Error(`Failed to fetch product: ${response.status}`);
        }

        const product = await response.json();

        // Use stored selected variant ID if available, otherwise use first available variant
        let selectedVariant = null;
        if (item.selectedVariantId) {
          selectedVariant = product.variants.find(variant => variant.id === parseInt(item.selectedVariantId));
        }

        // Fallback to first available variant ONLY if stored variant not found (don't override if user selected unavailable)
        if (!selectedVariant) {
          selectedVariant = product.variants.find(variant => variant.available) || product.variants[0];
        }

        if (!selectedVariant) {
          return null;
        }

        // Get currency from product API response
        const currency = product.currency;

        // Format prices with currency
        const priceFormatted = this.formatPrice(selectedVariant.price, currency);
        const comparePriceFormatted = selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price
          ? this.formatPrice(selectedVariant.compare_at_price, currency)
          : '';

        // Check pre-sale status (use stored value or check inventory policy)
        const isPresale = this.checkPresaleStatus(selectedVariant, item.presale);

        // Check if product is sold out
        const isSoldOut = !selectedVariant.available || (selectedVariant.inventory_quantity === 0 && selectedVariant.inventory_policy !== 'continue');

        // Determine status for pre-sale logic
        let status = 'available';
        if (!selectedVariant.available) {
          status = 'sold_out';
        } else if (selectedVariant.inventory_policy === 'continue' && selectedVariant.inventory_quantity === 0 && selectedVariant.available) {
          status = 'pre_sale';
        } else if (selectedVariant.inventory_quantity === 0 && selectedVariant.available) {
          status = 'sold_out';
        }

        // Show pre-sale only if status is 'pre_sale' or if status is 'available' and presale metafield is true
        const showPresale = status === 'pre_sale' || (status === 'available' && isPresale);

        const productData = {
          title: product.title,
          url: `/products/${product.handle}`,
          image: product.featured_image || '',
          price: priceFormatted,
          comparePrice: comparePriceFormatted,
          variant: selectedVariant.title,
          variantId: selectedVariant.id,
          presale: showPresale,
          soldOut: isSoldOut
        };

        // Get pre-sale and sold out text from drawer
        const presaleText = this.elements.drawer?.getAttribute('data-presale-text') || 'This item will be reserved for you upon checkout.';
        const soldOutText = this.elements.drawer?.getAttribute('data-soldout-text') || 'This piece is currently archived and not available for acquisition.';

        // Check if there's a discount
        const hasDiscount = productData.comparePrice && productData.comparePrice.trim() !== '';

        // Check if variant should be displayed (not "Default Title")
        const shouldShowVariant = productData.variant && productData.variant !== 'Default Title';

        // Create element more efficiently
        const itemDiv = document.createElement('div');
        itemDiv.className = 'cart-item';
        itemDiv.setAttribute('role', 'row');
        itemDiv.id = `WishlistDrawer-Item-${item.id}`;

        // Build HTML string more efficiently
        // Get spinner template
        const spinnerTemplate = document.getElementById('WishlistLoadingSpinnerTemplate');
        const spinnerHtml = spinnerTemplate ? spinnerTemplate.innerHTML : '';

        const html = `
        <div class="cart-item__media" role="cell" headers="CartDrawer-ColumnProductImage" style="position: relative;">
          <a href="${productData.url}" class="cart-item__link" tabindex="-1" aria-hidden="true"></a>
          ${productData.image ? `
            <div class="wishlist-item__spinner" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 1;">
              ${spinnerHtml}
            </div>
            <img class="cart-item__image" 
              src="${productData.image}&width=300" 
              alt="${productData.title}" 
              loading="lazy" 
              width="150" 
              height="150" 
              style="border-radius:var(--default-border-radius);object-fit:cover; opacity: 0; transition: opacity 0.3s ease;"
              onload="this.style.opacity='1'; this.previousElementSibling.style.display='none';">
          ` : ''}
        </div>
        <div class="cart-item__info">
          <div class="cart-item__details" role="cell" headers="CartDrawer-ColumnProduct">
            <div style="display: flex; align-items: center; gap: 0.5rem;">
              <a href="${productData.url}" class="wishlist-item__name break">${productData.title}</a>
              <button class="wishlist-button is-in-wishlist" data-product-id="${product.id}" data-wishlist-button style="width: 2.5rem; height: 2.5rem; background: none; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; position: relative; top: 0;">
                <svg class="wishlist-button__icon wishlist-button__icon--on" fill="currentColor" stroke="currentColor" stroke-width="1" aria-hidden="true" focusable="false" style="width: 14px; height: 28px; opacity: 1; color: rgb(var(--color-foreground));" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </button>
            </div>
            ${hasDiscount ? `
              <div class="cart-item__discounted-prices" style="display: flex; align-items: center; gap: 0.5rem;">
                <span class="visually-hidden">Regular price</span>
                <s class="cart-item__old-price product-option caption-small">${productData.comparePrice}</s>
                <span class="visually-hidden">Sale price</span>
                <strong class="cart-item__final-price product-option caption-small">${productData.price}</strong>
                ${productData.soldOut ? `
                  <span class="badge price__badge-sold-out color-scheme-3" style="font-size: 0.75rem; padding: 0.25rem 0.5rem;">
                    ${window.variantStrings.soldOut}
                  </span>
                ` : ''}
              </div>
            ` : `
              <div class="product_price-option product-option caption-small" style="display: flex; align-items: center; gap: 0.5rem;">
                <span>${productData.price}</span>
                ${productData.soldOut ? `
                  <span class="badge price__badge-sold-out color-scheme-3" style="font-size: 0.75rem; padding: 0.25rem 0.5rem;">
                    ${window.variantStrings.soldOut}
                  </span>
                ` : ''}
              </div>
            `}
            ${shouldShowVariant ? `
              <dl>
                <div class="product-option caption-small">
                  <dd>${productData.variant}</dd>
                </div>
              </dl>
            ` : ''}
            ${productData.presale ? `
              <div class="cart-item__pre-sale caption-small italic">
                ${presaleText}
              </div>
            ` : ''}
            ${productData.soldOut && !productData.presale ? `
              <div class="cart-item__pre-sale caption-small italic">
                ${soldOutText}
              </div>
            ` : ''}
          </div>
        </div>
      `;

        itemDiv.innerHTML = html;

        // Add remove functionality to the wishlist button
        const wishlistButton = itemDiv.querySelector('.wishlist-button');
        wishlistButton.addEventListener('click', () => {
          // Remove the product completely from wishlist (regardless of variant)
          this.removeProduct(product.id);

          // Remove the item from DOM immediately
          itemDiv.remove();

          // Update drawer state if needed
          const isEmpty = Object.keys(this.wishlist).length === 0;
          if (isEmpty) {
            // Only update drawer state if it's completely empty
            this.showEmptyState();
          }

          // Update total without rebuilding entire drawer
          this.updateWishlistTotal();

          // Check for presale items and show/hide reserve terms
          this.checkPresaleItemsAndShowTerms();
          this.updateDrawerPresaleStatus();

          // Update "Add All to Cart" button state based on archived items
          this.updateAddAllButtonState();

          // Update all wishlist buttons for this product
          document.querySelectorAll(`[data-wishlist-button][data-product-id="${product.id}"]`).forEach(btn => {
            btn.classList.remove('is-in-wishlist');
            btn.setAttribute('aria-label', 'Add to wishlist');
          });
        });

        return itemDiv;

      } catch (error) {
        console.error('Error creating wishlist item:', error);
        return null;
      }
    }

    openDrawer() {
      const drawer = document.getElementById('WishlistDrawer');
      if (!drawer) return;

      // Pre-load wishlist content before opening to prevent lag
      this.preloadWishlistContent().then(() => {
        if (drawer?.open) {
          drawer.open();
        } else if (typeof initializeDrawer === 'function') {
          initializeDrawer('WishlistDrawer', 'WishlistTrigger');
        }
      });
    }

    async preloadWishlistContent() {
      // If drawer is already loaded, don't preload again
      const wishlistItems = this.elements.drawer?.querySelector('#WishlistItems');
      if (wishlistItems && wishlistItems.children.length > 0) {
        return;
      }

      // Pre-load content in background
      const isEmpty = Object.keys(this.wishlist).length === 0;
      if (!isEmpty) {
        await this.updateDrawerContent();
      }
    }

    dispatchUpdateEvent() {
      window.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { wishlist: this.wishlist }
      }));
    }

    // Initialize wishlist buttons state
    initializeWishlistButtons() {
      const wishlistButtons = document.querySelectorAll('[data-wishlist-button]');

      wishlistButtons.forEach((button) => {
        const productId = button.getAttribute('data-product-id');
        if (!productId) return;

        // Get variant ID if available
        let variantId = null;
        if (button.getAttribute('data-is-product-page') === 'true') {
          variantId = button.getAttribute('data-current-variant-id');
        }

        // Check if product is already in wishlist
        const isInWishlist = this.isInWishlist(productId, variantId);

        if (isInWishlist) {
          button.classList.add('is-in-wishlist');
          button.setAttribute('aria-label', 'Remove from wishlist');
        } else {
          button.classList.remove('is-in-wishlist');
          button.setAttribute('aria-label', 'Add to wishlist');
        }
      });
    }

    // Public methods for external use
    getWishlist() {
      return this.wishlist;
    }

    isInWishlist(productId, variantId = null) {
      const productIdInt = parseInt(productId);
      if (isNaN(productIdInt)) return false;

      // Check by product+variant key first
      if (variantId) {
        const wishlistKey = `${productIdInt}-${variantId}`;
        if (this.wishlist.hasOwnProperty(wishlistKey)) {
          return true;
        }
      }

      // Check by product ID only (for backward compatibility)
      if (this.wishlist.hasOwnProperty(productIdInt)) {
        return true;
      }

      // Check if any variant of this product is in wishlist
      const productKeys = Object.keys(this.wishlist).filter(key =>
        key.startsWith(`${productIdInt}-`) || key === productIdInt.toString()
      );
      return productKeys.length > 0;
    }

    // Helper method to format price
    formatPrice(price, currency = null) {
      if (!price) return '';

      // Convert cents to dollars
      const amount = price / 100;

      // Format amount to remove unnecessary decimals
      const formattedAmount = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);

      // Use provided currency or try to get from drawer
      let currencyCode = currency || 'EUR';

      if (!currency) {
        const drawer = document.getElementById('WishlistDrawer');
        if (drawer) {
          const drawerCurrency = drawer.getAttribute('data-currency-code');
          if (drawerCurrency) {
            currencyCode = drawerCurrency;
          }
        }
      }

      // Always use currency code format for wishlist (45 EUR)
      return `${formattedAmount} ${currencyCode}`;
    }

    // Helper method to check presale status
    checkPresaleStatus(variant, storedPresale = false) {
      if (!variant) return storedPresale;

      // If we have stored presale info, use it (from metafields)
      if (storedPresale) return true;

      // Check inventory policy (this is available in the API response)
      if (variant.inventory_policy === 'continue' && variant.inventory_quantity === 0 && variant.available) {
        return true;
      }

      return false;
    }

    // Add all wishlist items to cart
    async addAllToCart() {
      const productIds = Object.keys(this.wishlist);
      if (productIds.length === 0) return;

      const button = document.getElementById('WishlistDrawer-AddAllToCart');
      const cartDrawer = document.querySelector('cart-drawer');

      if (!cartDrawer) {
        console.error('Cart drawer not found');
        return;
      }

      // Disable button and show loading (like product-form.js)
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }

      this.error = false;

      try {

        // Get all product data first
        const productPromises = productIds.map(async (productId) => {
          const item = this.wishlist[productId];
          if (!item || !item.handle) return null;

          try {
            const response = await fetch(`/products/${item.handle}.js`);
            if (!response.ok) {
              // If product doesn't exist (404), remove it from wishlist
              if (response.status === 404) {
                console.warn(`Product ${item.handle} no longer exists, removing from wishlist`);
                this.removeProduct(productId);
                return null;
              }
              return null;
            }

            const productData = await response.json();

            // Use stored selected variant ID if available, otherwise use first available variant
            let selectedVariant = null;
            if (item.selectedVariantId) {
              selectedVariant = productData.variants.find(variant => variant.id === item.selectedVariantId);
            }

            // Fallback to first available variant if stored variant not found or not available
            if (!selectedVariant || !selectedVariant.available) {
              selectedVariant = productData.variants.find(variant => variant.available) || productData.variants[0];
            }

            if (!selectedVariant) return null;

            // Check if this variant is sold out
            const isSoldOut = !selectedVariant.available || (selectedVariant.inventory_quantity === 0 && selectedVariant.inventory_policy !== 'continue');

            // Skip sold out products
            if (isSoldOut) {
              console.warn(`Skipping sold out product: ${productData.title} (${selectedVariant.title})`);
              return null;
            }

            return {
              id: selectedVariant.id,
              quantity: 1,
              product: productData
            };
          } catch (error) {
            console.error(`Error fetching product ${item.handle}:`, error);
            return null;
          }
        });

        const products = (await Promise.all(productPromises)).filter(p => p !== null);
        console.log(`Found ${products.length} available products out of ${productIds.length} total wishlist items`);
        if (products.length === 0) {
          console.warn('No available products found to add to cart');
          return;
        }

        // Track successfully added products to remove from wishlist
        const successfullyAddedProducts = [];

        // Add products one by one (like product-form.js does for single products)
        for (const product of products) {

          console.log(`Processing product: ${product.product.title} (${product.product.variants.find(v => v.id === product.id)?.title || 'Unknown variant'})`);

          // Note: We're adding all products to cart, even if they're already there
          // This allows users to have multiple quantities of the same item

          // Double-check that the product is available before adding to cart
          if (!product.product || !product.product.variants) {
            continue;
          }

          const variant = product.product.variants.find(v => v.id === product.id);
          if (!variant || !variant.available || (variant.inventory_quantity === 0 && variant.inventory_policy !== 'continue')) {
            console.warn(`Skipping sold out product: ${product.product.title} (${variant?.title || 'Unknown variant'})`);
            continue;
          }

          // Create form data for single product (exactly like product-form.js)
          const formData = new FormData();
          formData.append('id', product.id);
          formData.append('quantity', product.quantity);

          // Add custom property to make each wishlist item unique in cart
          formData.append('properties[_wishlist_item]', 'true');
          formData.append('properties[_wishlist_timestamp]', Date.now().toString());

          // Add sections for cart drawer update (exactly like product-form.js)
          formData.append(
            'sections',
            cartDrawer.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cartDrawer.setActiveElement(document.activeElement);

          // Use the same fetch config as product-form.js
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = formData;

          // Add to cart using the same endpoint as product-form.js
          const response = await fetch(`${routes.cart_add_url}`, config);
          const cartData = await response.json();

          if (cartData.status) {
            console.error('Error adding product to cart:', cartData);
            // Continue with next product instead of stopping
            continue;
          }

          // Product was successfully added to cart
          successfullyAddedProducts.push(product);
          console.log(`Successfully added ${product.product.title} to cart`);

          // Use the same cart update system as product-form.js
          if (typeof publish === 'function') {
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'wishlist-add-all',
              productVariantId: product.id,
              cartData: cartData
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });
          }

          // Update cart drawer using the same method as product-form.js
          CartPerformance.measure("add:paint-updated-sections", () => {
            cartDrawer.renderContents(cartData);
          });

          // Small delay to avoid overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 150));
        }

        // Remove only successfully added products from wishlist
        successfullyAddedProducts.forEach(product => {
          // Find the wishlist key for this product
          const productKeys = Object.keys(this.wishlist).filter(key => {
            // Check if this key corresponds to the product we just added
            if (key.includes('-')) {
              // Key format: productId-variantId
              const [productId] = key.split('-');
              return productId === product.product.id.toString();
            } else {
              // Key format: productId
              return key === product.product.id.toString();
            }
          });

          // Remove all variants of this product from wishlist
          productKeys.forEach(key => {
            delete this.wishlist[key];
          });
        });

        this.saveWishlist();
        this.updateCount();
        this.dispatchUpdateEvent();

        // Update wishlist buttons only for successfully added products
        successfullyAddedProducts.forEach(product => {
          document.querySelectorAll(`[data-wishlist-button][data-product-id="${product.product.id}"]`).forEach(btn => {
            btn.classList.remove('is-in-wishlist');
            btn.setAttribute('aria-label', 'Add to wishlist');
          });
        });

        // Update drawer state
        const isEmpty = Object.keys(this.wishlist).length === 0;
        if (isEmpty) {
          this.showEmptyState();
        } else {
          this.hideEmptyState();
          // Rebuild drawer content to reflect remaining items
          await this.updateDrawerContent();
        }
        this.updateWishlistTotal();

        console.log(`Add to cart completed: ${successfullyAddedProducts.length} products added, ${productIds.length - successfullyAddedProducts.length} products remained in wishlist`);

        // Close wishlist drawer after all products are added
        const wishlistDrawer = document.getElementById('WishlistDrawer');
        if (wishlistDrawer && typeof wishlistDrawer.close === 'function') {
          wishlistDrawer.close();
        }

      } catch (error) {
        console.error('Error adding all items to cart:', error);
        this.error = true;
      } finally {
        // Re-enable button and hide loading (exactly like product-form.js)
        if (button) {
          button.classList.remove('loading');
          const spinner = button.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
          if (cartDrawer && cartDrawer.classList.contains('is-empty')) {
            cartDrawer.classList.remove('is-empty');
          }
          if (!this.error) {
            button.removeAttribute('aria-disabled');
          }
        }
      }
    }

    async addSingleItemToDrawer(item) {
      const wishlistItems = this.elements.drawer?.querySelector('#WishlistItems');
      if (!wishlistItems) return;

      // Check if item already exists in drawer to prevent duplicates
      const existingItem = wishlistItems.querySelector(`#WishlistDrawer-Item-${item.id}`);
      if (existingItem) {
        return;
      }

      // If this is the first item being added, hide empty state
      const isEmpty = this.elements.drawer.classList.contains('is-empty');
      if (isEmpty) {
        this.hideEmptyState();
      }

      const itemElement = await this.createWishlistItem(item, wishlistItems.children.length);
      if (itemElement) {
        wishlistItems.appendChild(itemElement);
        this.updateWishlistTotal();

        // Check for presale items and show/hide reserve terms
        this.checkPresaleItemsAndShowTerms();
        this.updateDrawerPresaleStatus();
      }
      // Update "Add All to Cart" button state based on archived items
      this.updateAddAllButtonState();
    }

    updateWishlistTotal() {
      const totalElement = document.getElementById('WishlistDrawer-Total');
      if (!totalElement) return;

      const productIds = Object.keys(this.wishlist);

      if (productIds.length === 0) {
        totalElement.textContent = `0 ${this.getCurrencyCode()}`;
        return;
      }

      // For immediate update, just show the count instead of calculating total
      // This prevents flickering while the total is being calculated
      totalElement.textContent = `${productIds.length} items`;

      // Calculate the actual total in the background
      this.calculateTotalFromProducts();

      // Check for presale items and show/hide reserve terms
      this.checkPresaleItemsAndShowTerms();
      this.updateDrawerPresaleStatus();
    }

    async calculateTotalFromProducts() {
      const totalElement = document.getElementById('WishlistDrawer-Total');
      if (!totalElement) return;

      const productIds = Object.keys(this.wishlist);
      if (productIds.length === 0) {
        totalElement.textContent = `0 ${this.getCurrencyCode()}`;
        return;
      }

      // Debounce the calculation to prevent multiple rapid calls
      if (this.totalCalculationTimeout) {
        clearTimeout(this.totalCalculationTimeout);
      }

      this.totalCalculationTimeout = setTimeout(async () => {
        let total = 0;
        let currencyCode = this.getCurrencyCode();

        // Fetch all products to calculate total
        const productPromises = productIds.map(async (productId) => {
          const item = this.wishlist[productId];
          if (!item || !item.handle) return null;

          try {
            const response = await fetch(`/products/${item.handle}.js`);
            if (!response.ok) {
              // If product doesn't exist (404), remove it from wishlist
              if (response.status === 404) {
                console.warn(`Product ${item.handle} no longer exists, removing from wishlist`);
                this.removeProduct(productId);
                return null;
              }
              return null;
            }

            const productData = await response.json();

            // Use stored selected variant ID if available, otherwise use first available variant
            let selectedVariant = null;
            if (item.selectedVariantId) {
              selectedVariant = productData.variants.find(variant => variant.id === item.selectedVariantId);
            }

            // Fallback to first available variant if stored variant not found or not available
            if (!selectedVariant || !selectedVariant.available) {
              selectedVariant = productData.variants.find(variant => variant.available) || productData.variants[0];
            }

            if (!selectedVariant) return null;

            return {
              price: selectedVariant.price,
              currency: productData.currency || currencyCode
            };
          } catch (error) {
            return null;
          }
        });

        const products = (await Promise.all(productPromises)).filter(p => p !== null);

        // Calculate total
        products.forEach(product => {
          total += product.price;
          currencyCode = product.currency || currencyCode;
        });

        // Format total and update only if the element still exists
        if (totalElement && document.contains(totalElement)) {
          const formattedTotal = this.formatPrice(total, currencyCode);
          totalElement.textContent = formattedTotal;
        }
      }, 100); // Small delay to batch multiple rapid updates
    }

    getCurrencyCode() {
      const drawer = document.getElementById('WishlistDrawer');
      if (drawer) {
        return drawer.getAttribute('data-currency-code') || 'EUR';
      }
      return 'EUR';
    }

    // Check for presale items and show/hide reserve terms
    async checkPresaleItemsAndShowTerms() {
      const reserveTermsElement = document.getElementById('WishlistReserveTerms');
      if (!reserveTermsElement) return;

      const productIds = Object.keys(this.wishlist);
      if (productIds.length === 0) {
        reserveTermsElement.style.display = 'none';
        return;
      }

      // Check if any items are presale
      let hasPresaleItems = false;

      for (const productId of productIds) {
        const item = this.wishlist[productId];
        if (!item || !item.handle) continue;

        try {
          const response = await fetch(`/products/${item.handle}.js`);
          if (!response.ok) continue;

          const product = await response.json();

          // Use stored selected variant ID if available, otherwise use first available variant
          let selectedVariant = null;
          if (item.selectedVariantId) {
            selectedVariant = product.variants.find(variant => variant.id === item.selectedVariantId);
          }

          // Fallback to first available variant if stored variant not found or not available
          if (!selectedVariant || !selectedVariant.available) {
            selectedVariant = product.variants.find(variant => variant.available) || product.variants[0];
          }

          if (!selectedVariant) continue;

          // Check presale status (same logic as createWishlistItem)
          const isPresale = this.checkPresaleStatus(selectedVariant, item.presale);

          // Determine status for pre-sale logic
          let status = 'available';
          if (!selectedVariant.available) {
            status = 'sold_out';
          } else if (selectedVariant.inventory_policy === 'continue' && selectedVariant.inventory_quantity === 0 && selectedVariant.available) {
            status = 'pre_sale';
          } else if (selectedVariant.inventory_quantity === 0 && selectedVariant.available) {
            status = 'sold_out';
          }

          // Show pre-sale only if status is 'pre_sale' or if status is 'available' and presale metafield is true
          const showPresale = status === 'pre_sale' || (status === 'available' && isPresale);

          if (showPresale) {
            hasPresaleItems = true;
            break; // Found at least one presale item, no need to check more
          }
        } catch (error) {
          console.warn('Error checking presale status for product:', item.handle, error);
          continue;
        }
      }

      // Show/hide reserve terms based on presale items
      if (hasPresaleItems) {
        reserveTermsElement.style.display = 'block';
      } else {
        reserveTermsElement.style.display = 'none';
      }
    }

    // Update the drawer's data-has-presale attribute based on current wishlist items
    async updateDrawerPresaleStatus() {
      const drawer = this.elements.drawer;
      if (!drawer) return;

      const productIds = Object.keys(this.wishlist);
      if (productIds.length === 0) {
        drawer.setAttribute('data-has-presale', 'false');
        return;
      }

      // Check if any items are presale
      let hasPresaleItems = false;

      for (const productId of productIds) {
        const item = this.wishlist[productId];
        if (!item || !item.handle) continue;

        try {
          const response = await fetch(`/products/${item.handle}.js`);
          if (!response.ok) continue;

          const product = await response.json();

          // Use stored selected variant ID if available, otherwise use first available variant
          let selectedVariant = null;
          if (item.selectedVariantId) {
            selectedVariant = product.variants.find(variant => variant.id === item.selectedVariantId);
          }

          // Fallback to first available variant if stored variant not found or not available
          if (!selectedVariant || !selectedVariant.available) {
            selectedVariant = product.variants.find(variant => variant.available) || product.variants[0];
          }

          if (!selectedVariant) continue;

          // Check presale status (same logic as createWishlistItem)
          const isPresale = this.checkPresaleStatus(selectedVariant, item.presale);

          // Determine status for pre-sale logic
          let status = 'available';
          if (!selectedVariant.available) {
            status = 'sold_out';
          } else if (selectedVariant.inventory_policy === 'continue' && selectedVariant.inventory_quantity === 0 && selectedVariant.available) {
            status = 'pre_sale';
          } else if (selectedVariant.inventory_quantity === 0 && selectedVariant.available) {
            status = 'sold_out';
          }

          // Show pre-sale only if status is 'pre_sale' or if status is 'available' and presale metafield is true
          const showPresale = status === 'pre_sale' || (status === 'available' && isPresale);

          if (showPresale) {
            hasPresaleItems = true;
            break; // Found at least one presale item, no need to check more
          }
        } catch (error) {
          console.warn('Error checking presale status for product:', item.handle, error);
          continue;
        }
      }

      // Update the drawer's data-has-presale attribute
      drawer.setAttribute('data-has-presale', hasPresaleItems.toString());

      // Trigger the reserve terms handler to update
      if (window.reserveTermsHandler) {
        window.reserveTermsHandler.updatePresaleStatus(drawer, hasPresaleItems);
      }
    }

  };
}

// Initialize wishlist when DOM is ready
const initWishlist = () => {
  if (!window.wishlist) {
    window.wishlist = new Wishlist();
  } else {
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlist);
} else {
  initWishlist();
}

// Re-initialize wishlist buttons when new content is loaded (for AJAX/navigation)
document.addEventListener('DOMContentLoaded', () => {
  // Observer for dynamic content changes
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        // Check if new wishlist buttons were added
        const hasNewWishlistButtons = Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            return node.querySelector('[data-wishlist-button]') || node.hasAttribute('data-wishlist-button');
          }
          return false;
        });

        if (hasNewWishlistButtons && window.wishlist) {
          // Small delay to ensure DOM is fully updated
          setTimeout(() => {
            window.wishlist.initializeWishlistButtons();
          }, 100);
        }
      }
    });
  });

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}); 